import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon, Package, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatRetailerName } from "@/lib/formatRetailer";
import { useSlotAvailability } from "@/hooks/useSlotAvailability";

interface UserPackageInfo {
  id: string;
  retailer: string;
  size: string;
  return_by_date?: string | null;
  approval_status: string;
}

interface ProposePickupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userData: {
    userId: string;
    userName: string;
    email: string;
    address: string;
    packages: UserPackageInfo[];
    subscriptionType?: string | null;
    existingPickupsThisMonth?: number;
  } | null;
  managers: { id: string; name: string }[];
  onPropose: (data: {
    userId: string;
    proposedDate: Date;
    proposedTimeSlot: string;
    managerId: string | null;
    address: string;
    packageIds: string[];
  }) => Promise<void>;
  loading?: boolean;
}

const TIME_SLOTS = [
  { value: "9:00 AM – 12:00 PM", label: "9:00 AM – 12:00 PM" },
  { value: "12:00 PM – 3:00 PM", label: "12:00 PM – 3:00 PM" },
  { value: "3:00 PM – 6:00 PM", label: "3:00 PM – 6:00 PM" },
];

export const ProposePickupDialog = ({
  open,
  onOpenChange,
  userData,
  managers,
  onPropose,
  loading = false,
}: ProposePickupDialogProps) => {
  const [proposedDate, setProposedDate] = useState<Date>();
  const [proposedTimeSlot, setProposedTimeSlot] = useState<string>("");
  const [selectedManager, setSelectedManager] = useState<string>("none");
  const { availability, loading: checkingAvailability, checkDateAvailability, isSlotAvailable, getSlotStatus } = useSlotAvailability();

  // Reset form when dialog opens/closes or user changes
  useEffect(() => {
    if (open && userData) {
      setProposedDate(undefined);
      setProposedTimeSlot("");
      setSelectedManager("none");
    }
  }, [open, userData?.userId]);

  // Check availability when date changes
  useEffect(() => {
    if (proposedDate) {
      checkDateAvailability(proposedDate);
      // Clear time slot if previously selected slot is now full
      if (proposedTimeSlot && !isSlotAvailable(proposedTimeSlot)) {
        setProposedTimeSlot("");
      }
    }
  }, [proposedDate, checkDateAvailability]);

  const handlePropose = async () => {
    if (!userData || !proposedDate || !proposedTimeSlot) return;

    const approvedPackageIds = userData.packages
      .filter((p) => p.approval_status === "approved")
      .map((p) => p.id);

    await onPropose({
      userId: userData.userId,
      proposedDate,
      proposedTimeSlot,
      managerId: selectedManager === "none" ? null : selectedManager,
      address: userData.address,
      packageIds: approvedPackageIds,
    });
  };

  if (!userData) return null;

  const approvedPackages = userData.packages.filter(
    (p) => p.approval_status === "approved"
  );
  
  // Check subscription limit
  const getMonthlyLimit = () => {
    if (!userData.subscriptionType) return 1;
    if (userData.subscriptionType.toLowerCase().includes("bi-weekly") || 
        userData.subscriptionType.toLowerCase().includes("biweekly")) {
      return 2;
    }
    return 1;
  };
  
  const monthlyLimit = getMonthlyLimit();
  const remainingPickups = monthlyLimit - (userData.existingPickupsThisMonth || 0);
  const canSchedule = remainingPickups > 0;

  // Find earliest return-by date for prioritization
  const earliestReturnBy = approvedPackages
    .filter((p) => p.return_by_date)
    .sort((a, b) => new Date(a.return_by_date!).getTime() - new Date(b.return_by_date!).getTime())[0]
    ?.return_by_date;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Propose Pickup for {userData.userName}</DialogTitle>
          <DialogDescription>
            Schedule a single pickup for all approved packages at this user's address.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Info */}
          <div className="p-3 bg-muted/50 rounded-lg space-y-1">
            <p className="text-sm font-medium">{userData.email}</p>
            <p className="text-sm text-muted-foreground">{userData.address}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline">
                {remainingPickups} of {monthlyLimit} pickup{monthlyLimit > 1 ? "s" : ""} remaining
              </Badge>
              {userData.subscriptionType && (
                <Badge variant="secondary">{userData.subscriptionType}</Badge>
              )}
            </div>
          </div>

          {/* Subscription Limit Warning */}
          {!canSchedule && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-destructive mt-0.5" />
              <p className="text-sm text-destructive">
                This user has used all pickups for this billing period.
              </p>
            </div>
          )}

          {/* Return By Date Alert */}
          {earliestReturnBy && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Priority: Earliest return deadline is{" "}
                <span className="font-medium">
                  {format(new Date(earliestReturnBy), "MMM d, yyyy")}
                </span>
              </p>
            </div>
          )}

          {/* Approved Packages */}
          <div className="space-y-2">
            <p className="text-sm font-medium">
              Packages to be picked up ({approvedPackages.length})
            </p>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {approvedPackages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="flex items-center justify-between p-2 bg-muted/30 rounded-md"
                >
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{formatRetailerName(pkg.retailer)}</span>
                    <Badge variant="outline" className="text-xs">
                      {pkg.size}
                    </Badge>
                  </div>
                  {pkg.return_by_date && (
                    <span className="text-xs text-amber-600">
                      Due: {format(new Date(pkg.return_by_date), "MMM d")}
                    </span>
                  )}
                </div>
              ))}
              {approvedPackages.length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  No approved packages to schedule
                </p>
              )}
            </div>
          </div>

          {/* Date Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Proposed Pickup Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !proposedDate && "text-muted-foreground"
                  )}
                  disabled={!canSchedule || approvedPackages.length === 0}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {proposedDate ? format(proposedDate, "PPP") : "Select a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-popover" align="start">
                <Calendar
                  mode="single"
                  selected={proposedDate}
                  onSelect={setProposedDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Slot Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Proposed Time Window</label>
            {!proposedDate ? (
              <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                Select a date first to see available time slots
              </p>
            ) : checkingAvailability ? (
              <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                Checking availability...
              </p>
            ) : (
              <Select
                value={proposedTimeSlot}
                onValueChange={setProposedTimeSlot}
                disabled={!canSchedule || approvedPackages.length === 0 || !proposedDate}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select time window" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  {TIME_SLOTS.map((slot) => {
                    const slotStatus = getSlotStatus(slot.value);
                    const isFull = slotStatus?.isFull ?? false;
                    const spotsLeft = slotStatus?.available ?? 0;

                    return (
                      <SelectItem 
                        key={slot.value} 
                        value={slot.value}
                        disabled={isFull}
                        className={cn(isFull && "opacity-50")}
                      >
                        <div className="flex items-center justify-between w-full gap-4">
                          <span>{slot.label}</span>
                          {isFull ? (
                            <span className="text-xs text-destructive font-medium flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Unavailable
                            </span>
                          ) : spotsLeft <= 2 && spotsLeft > 0 ? (
                            <span className="text-xs text-amber-600 font-medium">
                              {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
                            </span>
                          ) : null}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Manager Assignment (Optional) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Assign Manager (Optional)</label>
            <Select value={selectedManager} onValueChange={setSelectedManager}>
              <SelectTrigger>
                <SelectValue placeholder="Select a manager" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="none">Unassigned</SelectItem>
                {managers.map((manager) => (
                  <SelectItem key={manager.id} value={manager.id}>
                    {manager.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handlePropose}
            disabled={
              loading ||
              !proposedDate ||
              !proposedTimeSlot ||
              !canSchedule ||
              approvedPackages.length === 0
            }
          >
            {loading ? "Proposing..." : "Propose Pickup"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
